import React, { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes, faCheck, faSpinner, faExclamationTriangle, faQrcode, faCopy,
} from '@fortawesome/free-solid-svg-icons';
import { QRCodeSVG } from 'qrcode.react';
import { useToast } from './ui/Toast';

function RedemptionResultModal({
  isOpen,
  onClose,
  result, // { ok, status, validationCode, itemName, itemCost, message, isPending }
  loading,
}) {
  const { success: showSuccess } = useToast();
  const qrRef = useRef(null);

  if (!isOpen || !result) return null;

  const handleCopyCode = () => {
    if (result.validationCode) {
      navigator.clipboard.writeText(result.validationCode);
      showSuccess('Validation code copied to clipboard!');
    }
  };

  const handleDownloadQR = () => {
    if (qrRef.current) {
      // QRCodeSVG doesn't have .canvas — need to render to canvas or use toDataURL differently
      // For now, use a simpler approach: let user screenshot or we'll export as SVG
      const svg = qrRef.current?.querySelector('svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          const url = canvas.toDataURL('image/png');
          const a = document.createElement('a');
          a.href = url;
          a.download = `redemption-${Date.now()}.png`;
          a.click();
        };
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      }
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-content--redemption" onClick={(e) => e.stopPropagation()}>
          <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            <FontAwesomeIcon icon={faSpinner} spin size="2x" style={{ color: '#b8860b' }} />
            <p style={{ marginTop: '1rem', color: '#51606f' }}>Processing your redemption…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!result.ok) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-content--redemption" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <FontAwesomeIcon icon={faExclamationTriangle} size="2x" style={{ color: '#d97706' }} />
            <h2 style={{ margin: '.75rem 0', color: '#dc2626' }}>Redemption Failed</h2>
            <p style={{ color: '#51606f', marginBottom: '1.5rem' }}>{result.message}</p>
            <button className="btn btn--primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success case
  if (result.isPending) {
    // Pending redemption — show status, not validation code yet
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content modal-content--redemption" onClick={(e) => e.stopPropagation()}>
          <button className="modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
          <div style={{ textAlign: 'center' }}>
            <FontAwesomeIcon icon={faSpinner} size="2x" style={{ color: '#b8860b' }} />
            <h2 style={{ margin: '.75rem 0' }}>Redemption Pending</h2>
            <p style={{ color: '#51606f', marginBottom: '1rem' }}>
              Your redemption request for <strong>{result.itemName}</strong> ({result.itemCost} tokens) has been submitted.
            </p>
            <div
              style={{
                background: '#fef3c7', color: '#92400e', padding: '1rem', borderRadius: '6px',
                marginBottom: '1.5rem', fontSize: '.9rem',
              }}
            >
              <strong>Awaiting staff confirmation.</strong>
              <p style={{ margin: '.5rem 0 0' }}>
                A staff member at the business will confirm your redemption shortly.
              </p>
            </div>
            <button className="btn btn--primary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Instant redemption — show validation code + QR
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-content--redemption" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <div style={{ textAlign: 'center' }}>
          <FontAwesomeIcon icon={faCheck} size="2x" style={{ color: '#10b981' }} />
          <h2 style={{ margin: '.75rem 0' }}>Redemption Confirmed!</h2>
          <p style={{ color: '#51606f', marginBottom: '1rem' }}>
            <strong>{result.itemName}</strong> — {result.itemCost} token{result.itemCost !== 1 ? 's' : ''} redeemed.
          </p>

          {/* Validation code */}
          <div
            style={{
              background: '#f7f1de', border: '2px solid #d4a648', padding: '1rem',
              borderRadius: '8px', marginBottom: '1rem',
            }}
          >
            <p style={{ margin: '0 0 .5rem', fontSize: '.8rem', color: '#6b6552', fontWeight: 600 }}>
              VALIDATION CODE
            </p>
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '.5rem', fontFamily: 'monospace', fontSize: '1.3rem', fontWeight: 700,
                color: '#1d2a3a', marginBottom: '.75rem',
              }}
            >
              <span>{result.validationCode}</span>
              <button
                className="btn btn--ghost"
                style={{ padding: '.4rem .6rem', fontSize: '.8rem' }}
                onClick={handleCopyCode}
              >
                <FontAwesomeIcon icon={faCopy} /> Copy
              </button>
            </div>
            <p style={{ margin: 0, fontSize: '.8rem', color: '#6b6552' }}>
              Show this code to staff at the register.
            </p>
          </div>

          {/* QR code */}
          <div style={{ marginBottom: '1rem' }}>
            <p style={{ margin: '0 0 .5rem', fontSize: '.8rem', color: '#6b6552', fontWeight: 600 }}>
              <FontAwesomeIcon icon={faQrcode} /> OR SCAN
            </p>
            <div
              style={{
                display: 'flex', justifyContent: 'center',
                background: '#fff', padding: '1rem', borderRadius: '8px',
                border: '1px solid #d4a648',
              }}
            >
              <div ref={qrRef}>
                <QRCodeSVG
                  value={result.validationCode || 'redemption'}
                  size={160}
                  level="H"
                  includeMargin
                />
              </div>
            </div>
            <button
              className="btn btn--ghost"
              style={{ marginTop: '.75rem', fontSize: '.85rem' }}
              onClick={handleDownloadQR}
            >
              Download QR Code
            </button>
          </div>

          <button className="btn btn--primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

export default RedemptionResultModal;
